import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Step,
  StepLabel,
  Stepper,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { assignLead, changeLeadStatus, getLead, updateLead } from '../../../api/leads';
import { listLookup } from '../../../api/masterData';
import { useLeadDraftAutosave } from './useLeadDraftAutosave';
import {
  buildLeadPayload,
  emptyWizardValues,
  leadToWizardValues,
  resultsToDrafts,
  SubjectResultDraft,
  WizardValues,
} from './wizardTypes';
import { Step1PersonalDetails } from './Step1PersonalDetails';
import { Step2Education } from './Step2Education';
import { Step3StudyPreferences } from './Step3StudyPreferences';
import { Step4ParentDetails } from './Step4ParentDetails';
import { Step5Financial } from './Step5Financial';
import { Step6Marketing } from './Step6Marketing';
import { Step7Documents } from './Step7Documents';
import { Step8Review } from './Step8Review';

const STEPS = [
  'Personal Details',
  'Education',
  'Study Preference',
  'Parent Details',
  'Financial Information',
  'Marketing',
  'Documents',
  'Review',
];

interface LeadRegistrationWizardProps {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
  /** When set, reopens an existing Draft lead where the counselor left off. */
  resumeLeadId?: number | null;
}

const extractErrorMessage = (err: unknown): string =>
  isAxiosError(err) ? (err.response?.data?.message ?? 'Something went wrong') : 'Something went wrong';

export const LeadRegistrationWizard = ({ open, onClose, onSaved, resumeLeadId }: LeadRegistrationWizardProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const autosave = useLeadDraftAutosave();
  const lastAssignedRef = useRef<number | ''>('');

  const [activeStep, setActiveStep] = useState(0);
  const [values, setValues] = useState<WizardValues>(emptyWizardValues);
  const [olResults, setOlResults] = useState<SubjectResultDraft[]>([]);
  const [alResults, setAlResults] = useState<SubjectResultDraft[]>([]);
  const [localError, setLocalError] = useState<string | null>(null);
  const [finalizing, setFinalizing] = useState(false);

  const statusesQuery = useQuery({
    queryKey: ['lookup', '/master-data/lead-statuses'],
    queryFn: () => listLookup('/master-data/lead-statuses'),
  });

  useEffect(() => {
    if (!open) return;
    setLocalError(null);
    if (resumeLeadId) {
      (async () => {
        const lead = await getLead(resumeLeadId);
        setValues(leadToWizardValues(lead));
        setOlResults(resultsToDrafts(lead.olResults));
        setAlResults(resultsToDrafts(lead.alResults));
        lastAssignedRef.current = lead.assignedTo?.id ?? '';
        autosave.hydrate(lead);
        setActiveStep(Math.min(Math.max(lead.lastCompletedStep, 0), STEPS.length - 1));
      })();
    } else {
      setValues(emptyWizardValues);
      setOlResults([]);
      setAlResults([]);
      lastAssignedRef.current = '';
      autosave.reset();
      setActiveStep(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, resumeLeadId]);

  const setField = <K extends keyof WizardValues>(field: K, value: WizardValues[K]) =>
    setValues((prev) => ({ ...prev, [field]: value }));

  const isStep1Valid = !!(values.firstName.trim() && values.lastName.trim() && values.phone.trim() && values.leadSourceId);

  const handleClose = () => {
    // A draft may have been created/updated during this session even if the
    // counselor cancels out without finalizing — refresh the list so it shows up.
    if (autosave.leadId) onSaved();
    onClose();
  };

  const persistCurrentStep = async (lastCompletedStep: number) => {
    const payload = buildLeadPayload(values, olResults, alResults);
    const lead = await autosave.saveStep(payload, lastCompletedStep);
    if (!lead) return null;
    if (values.assignedToId && values.assignedToId !== lastAssignedRef.current) {
      try {
        await assignLead(lead.id, values.assignedToId);
        lastAssignedRef.current = values.assignedToId;
      } catch {
        // Non-fatal — counselor assignment can be retried from the lead detail page.
      }
    }
    return lead;
  };

  const handleSaveDraft = async () => {
    await persistCurrentStep(activeStep);
  };

  const handleNext = async () => {
    if (activeStep === 0 && !isStep1Valid) return;
    const lead = await persistCurrentStep(activeStep + 1);
    if (!lead) return;
    setActiveStep((s) => Math.min(s + 1, STEPS.length - 1));
  };

  const handleBack = () => setActiveStep((s) => Math.max(s - 1, 0));

  const finalize = async (): Promise<number | null> => {
    if (!autosave.leadId) return null;
    setFinalizing(true);
    setLocalError(null);
    try {
      await updateLead(autosave.leadId, { lastCompletedStep: STEPS.length });
      const newStatus = statusesQuery.data?.find((s) => s.name === 'New');
      if (newStatus) await changeLeadStatus(autosave.leadId, newStatus.id as number);
      return autosave.leadId;
    } catch (err) {
      setLocalError(extractErrorMessage(err));
      return null;
    } finally {
      setFinalizing(false);
    }
  };

  const handleSaveLead = async () => {
    const id = await finalize();
    if (id) {
      onSaved();
      handleClose();
    }
  };

  const handleSaveAndFollowUp = async () => {
    const id = await finalize();
    if (id) {
      onSaved();
      onClose();
      navigate(`/leads/${id}`, { state: { openTab: 'follow-ups' } });
    }
  };

  const handleSaveAndRegisterStudent = async () => {
    const id = await finalize();
    if (id) {
      onSaved();
      onClose();
      navigate(`/leads/${id}`, { state: { openConvert: true } });
    }
  };

  const isLastStep = activeStep === STEPS.length - 1;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>Lead Registration</DialogTitle>
      <Box sx={{ px: 3, pt: 1 }}>
        {isMobile ? (
          <Box>
            <Typography variant="caption" color="text.secondary">
              Step {activeStep + 1} of {STEPS.length} · {STEPS[activeStep]}
            </Typography>
            <LinearProgress
              variant="determinate"
              value={((activeStep + 1) / STEPS.length) * 100}
              sx={{ height: 6, borderRadius: 3, mt: 0.5 }}
            />
          </Box>
        ) : (
          <Stepper activeStep={activeStep} alternativeLabel>
            {STEPS.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        )}
      </Box>

      <DialogContent dividers sx={{ minHeight: 460 }}>
        <Box sx={{ mb: 2 }}>
          {(autosave.error || localError) && <Alert severity="error">{autosave.error || localError}</Alert>}
          {autosave.saving && <Typography variant="caption" color="text.secondary">Saving…</Typography>}
          {!autosave.saving && autosave.savedAt && (
            <Typography variant="caption" color="text.secondary">
              Draft saved {autosave.savedAt.toLocaleTimeString()}
            </Typography>
          )}
        </Box>

        {activeStep === 0 && <Step1PersonalDetails values={values} setField={setField} leadNumber={autosave.leadNumber} />}
        {activeStep === 1 && (
          <Step2Education
            values={values}
            setField={setField}
            olResults={olResults}
            setOlResults={setOlResults}
            alResults={alResults}
            setAlResults={setAlResults}
          />
        )}
        {activeStep === 2 && <Step3StudyPreferences values={values} setField={setField} />}
        {activeStep === 3 && <Step4ParentDetails values={values} setField={setField} />}
        {activeStep === 4 && <Step5Financial values={values} setField={setField} />}
        {activeStep === 5 && <Step6Marketing values={values} setField={setField} leadId={autosave.leadId} />}
        {activeStep === 6 && <Step7Documents leadId={autosave.leadId} />}
        {activeStep === 7 && <Step8Review leadId={autosave.leadId} />}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleClose}>Cancel</Button>
        <Box sx={{ flexGrow: 1 }} />
        <Button onClick={handleSaveDraft} disabled={autosave.saving}>
          Save draft
        </Button>
        {activeStep > 0 && (
          <Button onClick={handleBack} disabled={autosave.saving || finalizing}>
            Back
          </Button>
        )}
        {!isLastStep ? (
          <Button
            variant="contained"
            onClick={handleNext}
            disabled={autosave.saving || (activeStep === 0 && !isStep1Valid)}
          >
            Next
          </Button>
        ) : (
          <>
            <Button variant="outlined" disabled={finalizing || !autosave.leadId} onClick={handleSaveAndFollowUp}>
              Save &amp; Create Follow-up
            </Button>
            <Button variant="outlined" disabled={finalizing || !autosave.leadId} onClick={handleSaveAndRegisterStudent}>
              Save &amp; Register Student
            </Button>
            <Button variant="contained" disabled={finalizing || !autosave.leadId} onClick={handleSaveLead}>
              {finalizing ? 'Saving…' : 'Save Lead'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};
